import { Controller } from '@hotwired/stimulus';

// Goals v3 comment composer: the plain textarea upgrades to Ambiki's
// CKEditor (the same self-hosted BalloonEditor build every custom-form
// wysiwyg field uses, public/CKEditor5/ckeditor.js).
//
// Two mount modes. The CREATE form mounts lazily on first focus — the Goals
// page renders one composer per goal row, and mounting editors nobody has
// clicked would be pure page weight. The EDIT form mounts eagerly
// (mode: 'eager'): its textarea starts hidden holding the comment's plain
// text, and the editor starts from the stored HTML (the html value), which
// never sits on screen raw. Either way the editor writes its data back into
// the textarea on every change, so the form submits exactly what the plain
// field would have.
//
// The controller loads the build itself when the page has not, once per page
// (loadBalloonEditor). A composer is not always in the page's first render:
// the Goals page's filter swap brings them in with the roster HTML, and the
// comment streams and the inline edit bring in a form of their own, with no
// layout behind any of them to load a script. The load starts when a
// composer connects, so the editor is usually there by the first focus.
//
// The format target says what the textarea submits: "plain" until the
// editor holds it, "html" after. The server keeps a plain submission as
// typed (Comment.html_for_plain_text) rather than reading it as HTML, which
// dropped everything after a "<" and every line break. So a build that
// cannot load (or a mount that fails) leaves a plain textarea that still
// keeps its text, and a failed edit mount shows the comment's plain text,
// never its markup.
//
// The turbo-stream replace after each submit rebuilds the form; Stimulus
// reconnects this controller and the next focus mounts a fresh editor.
export const CKEDITOR_SRC = '/CKEditor5/ckeditor.js';

// Resolves with BalloonEditor, adding the build's script to the page when
// nothing has. The pending load rides on its script element, so every
// composer on the page waits on the same one; a failed load takes its script
// away again, so a later focus can try once more.
export function loadBalloonEditor() {
  if (window.BalloonEditor) return Promise.resolve(window.BalloonEditor);

  const loading = document.head.querySelector('script[data-goals-v3-comment-editor-build]');
  if (loading && loading.goalsV3Loading) return loading.goalsV3Loading;

  const script = document.createElement('script');
  script.src = CKEDITOR_SRC;
  script.async = true;
  script.dataset.goalsV3CommentEditorBuild = 'true';
  script.goalsV3Loading = new Promise((resolve, reject) => {
    script.addEventListener('load', () => {
      if (window.BalloonEditor) {
        resolve(window.BalloonEditor);
      } else {
        script.remove();
        reject(new Error('the CKEditor build loaded without BalloonEditor'));
      }
    });
    script.addEventListener('error', () => {
      script.remove();
      reject(new Error('the CKEditor build did not load'));
    });
  });
  document.head.appendChild(script);
  return script.goalsV3Loading;
}

// Text typed into the plain textarea, as the editor's HTML: escaped, with its
// line breaks kept.
export function plainTextToHtml(text) {
  const escaped = String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
  return escaped ? `<p>${escaped.replace(/\r\n?|\n/g, '<br>')}</p>` : '';
}

export default class extends Controller {
  static targets = ['input', 'editor', 'format'];

  static values = { mode: { type: String, default: 'lazy' }, html: String };

  connect() {
    this._mounted = false;
    this._markPlain();
    if (this.modeValue === 'eager') {
      this._mount();
    } else {
      // Start the build loading now, so it is usually there by the first focus.
      if (!window.BalloonEditor) loadBalloonEditor().catch(() => {});
      this._onFocus = () => this._mount();
      this.inputTarget.addEventListener('focus', this._onFocus, { once: true });
    }
  }

  disconnect() {
    this._disconnected = true;
    if (this._onFocus) this.inputTarget.removeEventListener('focus', this._onFocus);
    if (this._editor) {
      this._editor.destroy().catch(() => {});
      this._editor = null;
    }
  }

  async _mount() {
    if (this._mounted || this._mounting) return;
    this._mounting = true;
    let BalloonEditor;
    try {
      BalloonEditor = await loadBalloonEditor();
    } catch {
      // No build: the textarea is the composer, and what it holds is plain
      // text. Re-arm so a later focus tries the load again.
      this._mounting = false;
      this._usePlainTextarea();
      if (this._onFocus) this.inputTarget.addEventListener('focus', this._onFocus, { once: true });
      return;
    }
    this._mounting = false;
    if (this._disconnected || this._mounted) return;
    this._mounted = true;
    try {
      // Reveal the mount point BEFORE creating: CKEditor computes view
      // geometry at create time, and the textarea is only hidden once the
      // editor verifiably exists and is displayed — never before, so a
      // failed or invisible mount can't leave the composer gone.
      this.editorTarget.classList.remove('d-none');
      const editor = await BalloonEditor.create(this.editorTarget, {
        licenseKey: window.ckeditorConfigLicenseKey,
        placeholder: this.inputTarget.placeholder || '',
        // What is typed stays as typed: no 3/4 to ¾, no -> to an arrow (see shared/form/controls/_body).
        typing: { transformations: { remove: ['mathematical'] } },
      });
      this._editor = editor;
      // The edit form starts from the stored HTML; the create form from
      // whatever was typed into the plain textarea while the build loaded.
      editor.setData(this.modeValue === 'eager' ? this.htmlValue : plainTextToHtml(this.inputTarget.value));
      if (window.getComputedStyle(this.editorTarget).display === 'none') {
        throw new Error('comment editor mounted but is not displayed');
      }
      // From here the textarea carries the editor's HTML, so the form submits
      // it even when nothing is edited.
      this.inputTarget.value = editor.getData();
      editor.model.document.on('change:data', () => {
        this.inputTarget.value = editor.getData();
      });
      this.inputTarget.classList.add('d-none');
      this._markHtml();
      if (this.modeValue !== 'eager') editor.editing.view.focus();
    } catch {
      // Editor failed — the textarea stays (or comes back) as the composer.
      this._mounted = false;
      if (this._editor) {
        this._editor.destroy().catch(() => {});
        this._editor = null;
      }
      this._usePlainTextarea();
    }
  }

  // The plain textarea as the composer, holding plain text: what was typed
  // into it, or the edit form's copy of the comment's text. A failed mount
  // never wrote the editor's HTML into it.
  _usePlainTextarea() {
    this.inputTarget.classList.remove('d-none');
    this.editorTarget.classList.add('d-none');
    this._markPlain();
  }

  _markPlain() {
    if (this.hasFormatTarget) this.formatTarget.value = 'plain';
  }

  _markHtml() {
    if (this.hasFormatTarget) this.formatTarget.value = 'html';
  }
}
